import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type CurvePoint,
  type GetRecommendationsV1WorkspacesWorkspaceIdPdpsPdpIdRecommendationsGetData,
  type GetRecommendationsV1WorkspacesWorkspaceIdPdpsPdpIdRecommendationsGetError,
  type RecommendationMetric,
  type RecommendationsResponse,
  getRecommendationsV1WorkspacesWorkspaceIdPdpsPdpIdRecommendationsGet,
} from '../../generated';

/**
 * Frontend metric types (camelCase) matching @vizit/molecules types
 * These are local to the API package to avoid circular dependencies
 */
type ImpactBolts = 1 | 2 | 3 | 4;

interface AreaMetric {
  key: string;
  type: 'area';
  impactDelta: number;
  bolts?: ImpactBolts;
  scoreRange: { min: number; max: number };
  curve: CurvePoint[];
  breakpoints: [number, number];
  currentValue: number;
}

interface BarMetric {
  key: string;
  type: 'bar';
  impactDelta: number;
  bolts?: ImpactBolts;
  range: { min: number; max: number };
  optimalBand: number[];
  currentValue: number;
}

interface BooleanMetric {
  key: string;
  type: 'boolean';
  impactDelta: number;
  bolts?: ImpactBolts;
  currentValue: boolean;
}

type Metric = AreaMetric | BarMetric | BooleanMetric;

export interface GetRecommendationsResponse {
  metrics: Metric[];
  /** Score-explainer hero verdict, resolved by the API alongside the metrics. */
  scoreExplainHeroCertified: boolean;
  /** Score-explainer carousel verdict, resolved by the API alongside the metrics. */
  scoreExplainCarouselCertified: boolean;
}

/**
 * Type for 202 Accepted response (processing in progress)
 */
export interface GetRecommendationsAcceptedResponse {
  detail: string;
}

/**
 * Union type representing all possible responses from the recommendations endpoint
 * - GetRecommendationsResponse: 200 response with metrics
 * - GetRecommendationsAcceptedResponse: 202 response indicating processing in progress
 */
export type GetRecommendationsData = GetRecommendationsResponse | GetRecommendationsAcceptedResponse;

export type GetRecommendationsQueryVariables =
  GetRecommendationsV1WorkspacesWorkspaceIdPdpsPdpIdRecommendationsGetData['path'];

export type GetRecommendationsQuery = GetRecommendationsData;

/**
 * Transforms API RecommendationMetric (snake_case) to frontend Metric (camelCase)
 */
const isValidBolts = (value: unknown): value is ImpactBolts => value === 1 || value === 2 || value === 3 || value === 4;

const transformMetric = (apiMetric: RecommendationMetric): Metric => {
  // Cast becomes a no-op once `pnpm generate` picks up the bolts field from the synced OpenAPI spec.
  // The narrow guard keeps the UI safe if the BE ever drifts off the 1–4 contract — anything else maps to undefined.
  const rawBolts = (apiMetric as RecommendationMetric & { bolts?: unknown }).bolts;
  const base = {
    key: apiMetric.key,
    type: apiMetric.type as 'area' | 'bar' | 'boolean',
    impactDelta: apiMetric.impact_delta,
    bolts: isValidBolts(rawBolts) ? rawBolts : undefined,
  };

  if (apiMetric.type === 'area') {
    return {
      ...base,
      type: 'area',
      scoreRange: apiMetric.score_range
        ? { min: apiMetric.score_range.min, max: apiMetric.score_range.max }
        : { min: 0, max: 100 },
      curve: apiMetric.curve || [],
      breakpoints: [0, 0] as [number, number],
      currentValue: typeof apiMetric.current_value === 'number' ? Math.round(apiMetric.current_value) : 0,
    };
  }

  if (apiMetric.type === 'bar') {
    return {
      ...base,
      type: 'bar',
      range: apiMetric.range ? { min: apiMetric.range.min, max: apiMetric.range.max } : { min: 0, max: 100 },
      optimalBand: apiMetric.optimal_band || [],
      currentValue: typeof apiMetric.current_value === 'number' ? apiMetric.current_value : 0,
    };
  }

  // boolean type
  return {
    ...base,
    type: 'boolean',
    currentValue: typeof apiMetric.current_value === 'boolean' ? apiMetric.current_value : false,
  };
};

export const useGetRecommendationsQuery = <
  TData = GetRecommendationsQuery,
  TError = GetRecommendationsV1WorkspacesWorkspaceIdPdpsPdpIdRecommendationsGetError,
>(
  { workspace_id, pdp_id }: GetRecommendationsQueryVariables,
  options?: Omit<UseQueryOptions<GetRecommendationsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetRecommendationsQuery, TError, TData>({
    queryKey: ['GetRecommendations', { workspace_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getRecommendationsV1WorkspacesWorkspaceIdPdpsPdpIdRecommendationsGet({
        path: { workspace_id, pdp_id },
        throwOnError: true,
      });

      // Transform metrics response (200) to frontend format
      if (data && typeof data === 'object' && 'metrics' in data) {
        const response = data as RecommendationsResponse;
        return {
          metrics: response.metrics.map(transformMetric),
          scoreExplainHeroCertified: response.score_explain_hero_certified === true,
          scoreExplainCarouselCertified: response.score_explain_carousel_certified === true,
        };
      }

      return data as GetRecommendationsData;
    },
    ...options,
  });
};

useGetRecommendationsQuery.getKey = ({ workspace_id, pdp_id }: GetRecommendationsQueryVariables) => [
  'GetRecommendations',
  { workspace_id, pdp_id },
];

useGetRecommendationsQuery.fetcher = (
  { workspace_id, pdp_id }: GetRecommendationsQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getRecommendationsV1WorkspacesWorkspaceIdPdpsPdpIdRecommendationsGet({
      path: { workspace_id, pdp_id },
      throwOnError: true,
    });

    // Transform metrics response (200) to frontend format
    if (data && typeof data === 'object' && 'metrics' in data) {
      const response = data as RecommendationsResponse;
      return {
        metrics: response.metrics.map(transformMetric),
        scoreExplainHeroCertified: response.score_explain_hero_certified === true,
        scoreExplainCarouselCertified: response.score_explain_carousel_certified === true,
      };
    }

    // Return other responses unchanged (e.g., 202 Accepted with { detail: "..." })
    // This preserves processing state information for polling logic
    return data as GetRecommendationsData;
  };
};

useGetRecommendationsQuery.schema = {
  method: 'get',
  path: '/v1/workspaces/{workspace_id}/pdps/{pdp_id}/recommendations',
};
